// Generic GraphQL mutation execution for Kiddom API
// Provides admin query/mutation functions for the GraphQL endpoint

import { ResourceQuery, fieldList, IQueryParam } from './queryBuilder'
import { doQueries } from './query'

export interface IQueryInfo {
  resourceData: ResourceQuery
  params?: { [key: string]: IQueryParam }
  alias?: string
}

export interface DataResponse<T> {
  page_info: { current_page: number; per_page: number }
  total_count: number
  data: T[]
}

/**
 * GraphQL paginated response structure (before normalization)
 * The GraphQL API returns data with a dynamic key matching the resource name
 * e.g., { districts: [...], page_info: {...}, total_count: N }
 *
 * This is an intersection of:
 * - A Record with one dynamic key containing the data array
 * - Fixed pagination metadata (page_info, total_count)
 */
export type GraphQLPaginatedResponse<T> = Record<string, T[]> & {
  page_info: { current_page: number; per_page: number; has_next_page?: boolean }
  total_count: number
}

/**
 * Execute admin queries against the GraphQL API
 * @param queries - Array of query definitions
 * @param throwErrors - Whether to throw errors or return them in response
 * @returns Query results with data and optional errors
 */
export async function doAdminQueries<T = unknown>(
  queries: IQueryInfo[],
  throwErrors = true,
): Promise<{ data: Record<string, T>; errors?: { message: string }[] }> {
  const result = await doQueries<T>('/api/query', queries, throwErrors)
  if (result.errors?.length) {
    const error = result.errors.map((e) => e.message).join('\n')
    result.error = result.error ?? '' + error
  }
  return result
}

/**
 * Execute admin queries and return result instead of data
 * @param queries - Array of query definitions
 * @returns Query results with result key instead of data
 */
export async function doAdminQueriesWithResult<T = unknown>(
  queries: IQueryInfo[],
): Promise<{ result: Record<string, T>; errors?: { message: string }[] }> {
  const { data, ...rest } = await doAdminQueries<T>(queries, false)
  return { result: data, ...rest }
}

/**
 * Execute a paginated query with sorting and filtering
 *
 * Note: The GraphQL API returns data with a dynamic key matching the resource name.
 * This function normalizes the response to always use 'data' as the key.
 *
 * @param key - Resource key to query
 * @param fields - Fields to retrieve
 * @param pageInfo - Pagination parameters (per_page, current_page)
 * @param sort - Sort parameters (field, order)
 * @param filters - Filter criteria
 * @returns Paginated response with data and page info
 */
export async function doPaginationQuery<T = unknown>(
  key: string,
  fields: fieldList | undefined = [],
  pageInfo: { per_page: number; current_page: number },
  sort: { field: string; order: 'asc' | 'desc' },
  filters: Record<string, string | number | boolean>,
): Promise<DataResponse<T>> {
  const resourceData = {
    key,
    fields: [
      ...fields,
      { page_info: ['current_page', 'per_page', 'has_next_page'] },
      'total_count',
    ],
  }
  const response = await doAdminQueries<GraphQLPaginatedResponse<T>>(
    [{ resourceData, params: { ...pageInfo, ...sort, ...filters } }],
    false,
  )
  // Transform GraphQL response (dynamic key) to normalized response (fixed 'data' key)
  const gqlResponse = response.data[resourceData.key]
  const { [resourceData.key]: data, page_info, total_count } = gqlResponse
  return {
    page_info,
    total_count,
    data,
  }
}

/**
 * Execute a single admin mutation
 * @param resourceData - Resource query definition
 * @param params - Mutation parameters
 * @param throwErrors - Whether to throw errors or return them in response
 * @returns Mutation result for the specific resource
 */
export async function doAdminMutation<T = unknown>(
  resourceData: ResourceQuery,
  params: Record<string, IQueryParam> | undefined,
  throwErrors = true,
) {
  return doAdminQueries<T>([{ resourceData, params }], throwErrors).then(
    ({ data, errors }) => {
      return {
        data: data?.[resourceData.key],
        errors,
      }
    },
  )
}

// Re-export types from queryBuilder for convenience
export type { ResourceQuery, fieldList, IQueryParam }
